import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { PageTitleService } from '../../../shared/services';
import { SystemAdminService, BusinessTypeDto, CustomerTypeDto } from '../../../services/system-admin/system-admin.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../services/auth';
import { Router } from '@angular/router';
import { formatDate } from '@angular/common';
import { DataTable, TableColumn } from '../../../shared/components/data-table/data-table';
import { TableCellDef } from '../../../shared/directives/table-cell.directive';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Modal } from '../../../shared/components/modal/modal';
import { ToastService } from '../../../shared/services';

@Component({
  selector: 'app-business-types',
  imports: [DataTable, TableCellDef, FormsModule, ReactiveFormsModule, Modal],
  templateUrl: './business-types.html',
  styleUrl: './business-types.css',
})
export class BusinessTypes implements OnInit {
  private titleService = inject(PageTitleService);
  private systemAdminService = inject(SystemAdminService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private toastService = inject(ToastService);
  private search$ = new Subject<string>();

  businessTypes = signal<BusinessTypeDto[]>([]);
  customerTypes = signal<CustomerTypeDto[]>([]);
  loading = signal(false);
  page = 1;
  pageSize = 20;
  totalCount = 0;
  totalPages = 1;
  searchTerm = '';
  showModal = signal(false);
  editingItem = signal<BusinessTypeDto | null>(null);
  form: FormGroup;

  constructor() {
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((q) => {
        this.searchTerm = q;
        this.page = 1;
        this.loadBusinessTypes();
      });

    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(200)]],
      customerTypeId: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    if (!this.authService.isSystemAdmin()) {
      this.router.navigate(['/agendas']);
      return;
    }

    this.titleService.setPage({
      title: 'Tipos de negocio',
      subtitle: 'Administra los tipos de negocio del sistema',
      breadcrumbs: [{ name: 'Administración', url: '/system-admin' }],
    });
    this.loadCustomerTypes();
    this.loadBusinessTypes();
  }

  onSearch = (q: string) => this.search$.next(q);

  loadCustomerTypes(): void {
    this.systemAdminService.getCustomerTypes().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (types) => this.customerTypes.set(types),
      error: (err) => console.error('Error loading customer types:', err)
    });
  }

  loadBusinessTypes(): void {
    this.loading.set(true);
    this.systemAdminService.getBusinessTypes({
      page: this.page,
      pageSize: this.pageSize,
      searchTerm: this.searchTerm || undefined,
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (response) => {
        this.businessTypes.set(response.businessTypes);
        this.totalCount = response.totalCount;
        this.totalPages = response.totalPages;
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading business types:', err);
        this.loading.set(false);
      }
    });
  }

  onPageChange(page: number): void {
    this.page = page;
    this.loadBusinessTypes();
  }

  openCreateModal(): void {
    this.editingItem.set(null);
    this.form.reset();
    this.showModal.set(true);
  }

  openEditModal(item: BusinessTypeDto): void {
    this.editingItem.set(item);
    this.form.patchValue({
      name: item.name,
      customerTypeId: item.customerTypeId,
    });
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingItem.set(null);
    this.form.reset();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value;
    const item = this.editingItem();

    if (item) {
      // Update
      this.systemAdminService.updateBusinessType(item.businessTypeId, {
        name: value.name,
        customerTypeId: value.customerTypeId,
      }).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: () => {
          this.toastService.show('Tipo de negocio actualizado exitosamente', 'success');
          this.closeModal();
          this.loadBusinessTypes();
        },
        error: (err) => {
          this.toastService.show(err.error?.message || 'Error al actualizar tipo de negocio', 'error');
        }
      });
    } else {
      // Create
      this.systemAdminService.createBusinessType({
        name: value.name,
        customerTypeId: value.customerTypeId,
      }).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: () => {
          this.toastService.show('Tipo de negocio creado exitosamente', 'success');
          this.closeModal();
          this.loadBusinessTypes();
        },
        error: (err) => {
          this.toastService.show(err.error?.message || 'Error al crear tipo de negocio', 'error');
        }
      });
    }
  }

  delete(item: BusinessTypeDto): void {
    if (!confirm(`¿Estás seguro de eliminar "${item.name}"?`)) {
      return;
    }

    this.systemAdminService.deleteBusinessType(item.businessTypeId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.toastService.show('Tipo de negocio eliminado exitosamente', 'success');
        this.loadBusinessTypes();
      },
      error: (err) => {
        this.toastService.show(err.error?.message || 'Error al eliminar tipo de negocio', 'error');
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/system-admin']);
  }

  columns: TableColumn[] = [
    {
      header: 'Nombre',
      field: 'name',
    },
    {
      header: 'Tipo de Cliente',
      field: 'customerTypeName',
    },
    {
      header: 'Fecha Creación',
      field: 'createdAt',
      format: (v) => formatDate(v, 'dd/MM/yyyy', 'es-CO'),
    },
    {
      header: 'Acciones',
      cell: 'acciones',
      widthClass: 'w-40',
    },
  ];
}

